import React from "react";
import { Drawer } from "antd";
import { ScheduleOutlined } from "@ant-design/icons";
import moment from "moment";
import "./caseTurnaroundTime.scss";

// ─── Helpers & types ───────────────────────────────────────────────────────

const formatElapsed = (from: string, to: string): string => {
  if (!from || !to) return "—";
  const diffSeconds = moment(to).diff(moment(from), "seconds");
  if (diffSeconds <= 0) return "< 1s";
  const years = Math.floor(diffSeconds / 31536000);
  const days = Math.floor((diffSeconds % 31536000) / 86400);
  const hours = Math.floor((diffSeconds % 86400) / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  const seconds = diffSeconds % 60;
  if (years > 0) return `${years}y ${days}d`;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

interface TATItem {
  key: string;
  label: string;
  time: string;
  color: string;
  subLabel?: string;
  doctorName?: string;
}

interface TATGroup {
  groupKey: string;
  type: "preamble" | "assignment";
  assignmentNum?: number;
  doctorName?: string;
  groupTAT?: string;
  items: TATItem[];
}

const TAT_COLORS = {
  start: "#1747E9",
  created: "#1747E9",
  assigned: "#F3B21B",
  drafted: "#8898AA",
  finalized: "green",
};

const buildTATGroups = (data: any): { groups: TATGroup[]; startTime: string } => {
  const startTime: string = data.acquisition_time || data.created_at;

  const preambleItems: TATItem[] = [];
  preambleItems.push({
    key: "start",
    label: data.acquisition_time ? "Scan Acquired" : "Scan Uploaded",
    time: startTime,
    color: TAT_COLORS.start,
    subLabel: data.acquisition_time ? "DICOM Acquisition" : "Case Created in System",
  });
  if (data.acquisition_time && data.created_at) {
    preambleItems.push({
      key: "created",
      label: "Case Uploaded",
      time: data.created_at,
      color: TAT_COLORS.created,
      subLabel: "Case Created in System",
    });
  }
  preambleItems.sort((a, b) => moment(a.time).valueOf() - moment(b.time).valueOf());

  const groups: TATGroup[] = [
    { groupKey: "preamble", type: "preamble", items: preambleItems },
  ];

  const hasMultiple = (data.assignments?.length ?? 0) > 1;

  (data.assignments ?? []).forEach((assignment: any, idx: number) => {
    const doctorName: string | undefined = assignment.assigned_to_doctor_name || undefined;
    const assignmentItems: TATItem[] = [];

    if (assignment.assigned_time) {
      assignmentItems.push({
        key: `assigned_${idx}`,
        label: "Case Assigned",
        time: assignment.assigned_time,
        color: TAT_COLORS.assigned,
        doctorName,
      });
    }
    if (assignment.report_drafted_time) {
      assignmentItems.push({
        key: `drafted_${idx}`,
        label: "Report Drafted",
        time: assignment.report_drafted_time,
        color: TAT_COLORS.drafted,
        doctorName,
      });
    }
    if (assignment.reported_time) {
      assignmentItems.push({
        key: `finalized_${idx}`,
        label: "Report Finalized",
        time: assignment.reported_time,
        color: TAT_COLORS.finalized,
        subLabel: "Final report published",
        doctorName,
      });
    }

    assignmentItems.sort((a, b) => moment(a.time).valueOf() - moment(b.time).valueOf());

    const groupTAT =
      assignmentItems.length >= 2
        ? formatElapsed(
            assignmentItems[0].time,
            assignmentItems[assignmentItems.length - 1].time
          )
        : undefined;

    groups.push({
      groupKey: `assignment_${idx}`,
      type: "assignment",
      assignmentNum: hasMultiple ? idx + 1 : undefined,
      doctorName,
      groupTAT,
      items: assignmentItems,
    });
  });

  groups.sort((a, b) => {
    const aTime = a.items[0]?.time ?? "";
    const bTime = b.items[0]?.time ?? "";
    return moment(aTime).valueOf() - moment(bTime).valueOf();
  });

  return { groups, startTime };
};

// ─── Drawer content ──────────────────────────────────────────────────────────

const TATDrawerContent: React.FC<{ data: any }> = ({ data }) => {
  const { groups, startTime } = buildTATGroups(data);

  const allItems = groups.flatMap((g) => g.items);
  allItems.sort((a, b) => moment(a.time).valueOf() - moment(b.time).valueOf());
  const lastItem = allItems[allItems.length - 1];
  const totalTAT = lastItem ? formatElapsed(startTime, lastItem.time) : null;

  const patientName = (data.patient_name ?? "").replace(/_/g, " ");
  const metaParts = [
    data.age != null ? `${data.age} yrs` : null,
    data.gender ?? null,
  ].filter(Boolean).join(" · ");

  const reversedGroups = [...groups].reverse();

  return (
    <div className="case-turnaround-time__content">
      <div className="tat-info-bar">
        <div className="tat-info-bar__left">
          <span className="tat-info-bar__name">{patientName || "—"}</span>
          {metaParts && <span className="tat-info-bar__meta">{metaParts}</span>}
        </div>
        {totalTAT && (
          <div className="tat-info-bar__tat">
            <span className="tat-info-bar__tat-label">Total TAT</span>
            <span className="tat-info-bar__tat-value">{totalTAT}</span>
          </div>
        )}
      </div>

      <div className="tat-groups">
        {reversedGroups.map((group) => (
          <div
            key={group.groupKey}
            className={`tat-group tat-group--${group.type}`}
          >
            {group.type === "assignment" && (
              <div className="tat-group-header">
                <div className="tat-group-header__left">
                  <span className="tat-group-header__title">
                    {group.assignmentNum
                      ? `Report #${group.assignmentNum}`
                      : "Report"}
                  </span>
                  {group.doctorName && (
                    <span className="tat-group-header__doctor">
                      Dr. {group.doctorName}
                    </span>
                  )}
                </div>
                {group.groupTAT && (
                  <div className="tat-group-header__tat">
                    <span className="tat-group-header__tat-label">TAT</span>
                    <span className="tat-group-header__tat-value">{group.groupTAT}</span>
                  </div>
                )}
              </div>
            )}

            <div className="tat-custom-timeline">
              {[...group.items].reverse().map((item) => (
                <div key={item.key} className="tat-custom-timeline__item">
                  <div className="tat-custom-timeline__left">
                    {item.key !== "start" && (
                      <span className="tat-custom-timeline__elapsed">
                        {formatElapsed(startTime, item.time)}
                      </span>
                    )}
                  </div>

                  <div className="tat-custom-timeline__stem">
                    <div
                      className="tat-custom-timeline__dot"
                      style={{ backgroundColor: item.color }}
                    />
                  </div>

                  <div className="tat-custom-timeline__right">
                    <div className="tat-timeline-card">
                      <div className="tat-timeline-card__label">{item.label}</div>
                      {item.subLabel && (
                        <div className="tat-timeline-card__sublabel">{item.subLabel}</div>
                      )}
                      <div className="tat-timeline-card__time">
                        {moment(item.time).format("DD MMM YYYY, hh:mm:ss A")}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Public component ───────────────────────────────────────────────────────

export interface CaseTurnaroundTimeProps {
  visible: boolean;
  data: any | null;
  onClose: () => void;
}

const CaseTurnaroundTime: React.FC<CaseTurnaroundTimeProps> = ({
  visible,
  data,
  onClose,
}) => (
  <Drawer
    className="case-turnaround-time"
    destroyOnClose
    title={
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <ScheduleOutlined style={{ color: "#1747E9" }} />
        <span>Report Turnaround Time &amp; Timeline</span>
      </span>
    }
    placement="right"
    width={460}
    onClose={onClose}
    visible={visible}
    maskClosable
    footer={null}
  >
    {data && <TATDrawerContent data={data} />}
  </Drawer>
);

export default CaseTurnaroundTime;
